import React from "react";


import ArrowForwardIos from "@material-ui/icons/ArrowForwardIos";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import {
	SermonContentItem,
	SearchService,
	SearchServiceHelper,
	IllustrationContentItem,
	BibleChapterContentItem,
	BibleVerseContentItem,
} from "../../../../src/services/SearchService";
import { SupportedTranslations } from "../../../../src/entities/BibleTranslation";
import { BuilderPreferences } from "../../../../src/entities/BuilderPreferences";
import { EventTracking, SearchEntity } from "../../../../src/utilities/Tracking/EventTracking";
import ClipboardJS from "clipboard";
import classNames from "classnames";
import { Typography, Divider, CircularProgress, Drawer, ListSubheader, IconButton, Tooltip, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) =>({
	root: {},
	drawer: {
		width: "350px",
		textAlign: "left",
		flexShrink: 0,
		zIndex: theme.zIndex.drawer + 101,
		[theme.breakpoints.down("sm")]: {
			width: "90%",
		},
	},
	title: {
		background: "#666",
		color: "#FFF",
		textAlign: "left",
		fontSize: 18,
	},
	closeIcon: {
		float: "right",
		padding: 16,
	},
	copyButton: {
		borderRadius: "unset",
	},
	loading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(2),
	},
	content: {
		overflow: "auto",
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
	},
	contentBody: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(2),
	},
	chapterParagraph: {
		paddingTop: 5,
	},
	verseNumber: {
		fontSize: 10,
		verticalAlign: "top",
		color: "#676767",
		marginRight: 3,
	},
	bibleCopyright: {
		marginTop: 20,
	},
}));

interface Props {
	onDrawerClose: () => void;
	onClipboardCopy: (nameOfThingCopied: string) => void;
	setBuilderAlert: (message: string) => void;
	sermonId?: string;
	illustrationId?: string;
	scriptureReference?: string;
	preferences: BuilderPreferences;
}

type PropsWithClasses = Props & { classes: ReturnType<typeof useStyles> };

interface State {
	open: boolean;
	contentType: string;
	contentText: string;
	sermon?: SermonContentItem;
	illustration?: IllustrationContentItem;
	bibleChapter?: BibleChapterContentItem;
	copyContent?: string;
}

class ContentDrawerComponent extends React.Component<PropsWithClasses, State> {
	constructor(props: PropsWithClasses) {
		super(props);
		this.state = {
			open: false,
			contentType: props.sermonId ? "Sermon" : props.illustrationId ? "Illustration" : "Bible",
			contentText: "",
		};
	}

	componentDidMount() {
		this.setState({ open: true });
		this.getContent();
		let clipboard = new ClipboardJS("#content-copy");
		clipboard.on("success", () => {
			this.props.onClipboardCopy(this.state.contentType);
		});
	}

	async getContent() {
		if (this.props.sermonId) {
			const sermon = await SearchService.getSermon(this.props.sermonId);
			let contentText = this.getSermonText(sermon);
			this.setState({ sermon, contentText });
			EventTracking.Builder.onDrawerOpen("Sermon");
		} else if (this.props.illustrationId) {
			const illustration = await SearchService.getIllustration(this.props.illustrationId);
			let contentText = this.getIllustrationText(illustration);
			this.setState({ illustration, contentText });
			EventTracking.Builder.onDrawerOpen("Illustration");
		} else if (this.props.scriptureReference) {
			const bibleChapter = await SearchService.getBibleChapter(
				this.props.scriptureReference,
				this.props.preferences.bibleTranslationId ? this.props.preferences.bibleTranslationId : "NIV",
			);
			let contentText = this.getChapterTextForView(bibleChapter);
			let copyChapterContent =  this.getChapterTextForCopy(bibleChapter);
			this.setState({ bibleChapter, contentText, copyContent: copyChapterContent });
			EventTracking.Builder.onDrawerOpen("Bible");
		}
	}

	groupChapterVerses = (bibleChapter: BibleChapterContentItem) => {
		const versesGroupedByParagraph: [BibleVerseContentItem[]] = [[]];
		bibleChapter.verses.forEach((verse) => {
			if (verse.startsParagraph) {
				versesGroupedByParagraph.push([verse]);
			} else {
				let lastGroup = versesGroupedByParagraph[versesGroupedByParagraph.length - 1];
				lastGroup.push(verse);
			}
		});
		return versesGroupedByParagraph;
	};

	onCopy = () => {
		if (this.props.sermonId) {
			EventTracking.Builder.onCopy(SearchEntity.Sermon, "Sermon Drawer Interaction");
			this.copyBibleChapterTextToClipboard(SearchEntity.Sermon);
		} else if (this.props.illustrationId) {
			EventTracking.Builder.onCopy(SearchEntity.Illustration, "Illustration Drawer Interaction");
			this.copyBibleChapterTextToClipboard(SearchEntity.Illustration);
		} else if (this.props.scriptureReference) {
			EventTracking.Builder.onCopy(SearchEntity.Bible, "Bible Drawer Interaction");
			this.copyBibleChapterTextToClipboard(SearchEntity.Bible);
		}
	};

	copyBibleChapterTextToClipboard = (contentType: SearchEntity) => {
		let { setBuilderAlert, onClipboardCopy } = this.props;
		let copiedThing,
			nameOfThingCopied = undefined;

		if (contentType === SearchEntity.Bible) {
			if (this.state.bibleChapter === undefined) {
				setBuilderAlert("Error: could not get bible verse for copy.");
				return;
			}
			let { bibleChapter } = this.state;
			nameOfThingCopied = `${bibleChapter.bookName} ${bibleChapter.chapterNumber} - ${bibleChapter.translationId}`;
			copiedThing = this.getChapterTextForCopy(bibleChapter);
		} else if (contentType === SearchEntity.Illustration) {
			if (this.state.illustration === undefined) {
				setBuilderAlert("Error: could not get illustration for copy.");
				return;
			}
			let { illustration } = this.state;
			nameOfThingCopied = `${illustration.title} by ${illustration.authorFirstName} ${illustration.authorLastName}`;
			copiedThing = this.getIllustrationText(illustration);
		} else if (contentType === SearchEntity.Sermon) {
			if (this.state.sermon === undefined) {
				setBuilderAlert("Error: could not get sermon for copy.");
				return;
			}
			let { sermon } = this.state;
			nameOfThingCopied = `${sermon.title} by ${sermon.authorFirstName} ${sermon.authorLastName}`;
			copiedThing = this.getSermonText(sermon);
		} else {
			setBuilderAlert("Error: Unsupported copy type. Please contact support if the issue persists.");
			return;
		}

		if (copiedThing === undefined || nameOfThingCopied === undefined) {
			setBuilderAlert("Error: There was an unknown error when copying.");
			return;
		}

		if (!navigator.clipboard) {
			setBuilderAlert("Error: It appears your web browser does not support copying.");
			return;
		}

		try {
			navigator.clipboard.writeText(copiedThing);
			onClipboardCopy(nameOfThingCopied);
		} catch (err) {
			setBuilderAlert("Error: " + err);
			return;
		}
	};

	getChapterTextForCopy = (chapter: BibleChapterContentItem) => {
		const bibleChapter = {...chapter};
		let versesGroupedByParagraph = this.groupChapterVerses(bibleChapter);
		let { bibleSearchCopySetting } = this.props.preferences;

		let copiedText = versesGroupedByParagraph.reduce((verses, group) => {
			group.forEach((verse) => {
				let verseForCopy = verse.verseContent;
				if (verse.startsParagraph && verses !== "") {
					verses += "\n";
				}

				if (bibleSearchCopySetting !== "text") {
					let verseNumber = verse.verseNumberStart.toString();
					if (verse.verseNumberStart !== verse.verseNumberEnd) {
						verseNumber += `-${verse.verseNumberEnd}`;
					}
					verses += `(${verseNumber}) `;
				}

				if (bibleSearchCopySetting === "all") {
					while (verseForCopy.includes("<footnote>") || verseForCopy.includes("</footnote>")) {
						verseForCopy = verseForCopy.replace("<footnote>", " (Note:- ");
						if (this.props.preferences.bibleTranslationId === "DBY") {
							verseForCopy = verseForCopy.replace("</footnote>", ") ");
						} else {
							verseForCopy = verseForCopy.replace("</footnote>", ")");
						}
					}
				} else {
					const footnoteTagRegex = /<footnote>.*<\/footnote>/g;
					verseForCopy = verseForCopy.replace(footnoteTagRegex, "");
				}

				verseForCopy = verseForCopy.replace(/<(?:.|\n)*?>/gm, "");
				verseForCopy = verseForCopy.replace(/&nbsp;/g, "");
				verseForCopy = verseForCopy.replace(/&mdash;/g, "-");
				verseForCopy = verseForCopy.replace(/&ndash;/g, "-");

				verses += verseForCopy + " "; //need to add a space after each verse
			});
			return verses;
		}, "");

		return `${copiedText} \n${bibleChapter.bookName} ${bibleChapter.chapterNumber} - ${bibleChapter.translationId}`;
	};

	getSermonText = (sermon: SermonContentItem) => {
		return sermon.body.replace(/<\/p>/g, "\n\n").replace(/<(?:.|\n)*?>/gm, "");
	};

	getIllustrationText = (illustration: IllustrationContentItem) => {
		return illustration.body.replace(/<\/p>/g, "\n").replace(/<(?:.|\n)*?>/gm, "");
	};

	getChapterTextForView = (chapter: BibleChapterContentItem) => {
		const bibleChapter = {...chapter};
		let versesGroupedByParagraph = this.groupChapterVerses(bibleChapter);
		let verses = "";
		versesGroupedByParagraph.forEach((groupedVerses) => {
			verses += "\n";
			groupedVerses.forEach((verse) => {
				const footnoteStartRegex = /<footnote>/g;
				const footnoteEndRegex = /<\/footnote>/g;
				const removeFootnoteRegex = /<footnote>(?:.|\n)*?<\/footnote>/g
		
				if (verse.verseContent.match(footnoteStartRegex) && verse.verseContent.match(footnoteEndRegex)) {
					if(this.props.preferences.bibleSearchViewSetting === "all"){
						verse.verseContent = verse.verseContent.replace(footnoteStartRegex, "<em> (Note- ");
						verse.verseContent = verse.verseContent.replace(footnoteEndRegex, ")</em>");
					} else {
						verse.verseContent = verse.verseContent.replace(removeFootnoteRegex, "")
					}
				}
				verse.verseContent.replace("<red-letter>", '<mark class="pen-red">').replace("</red-letter>", "</mark>");
				verses += `\n${verse.verseNumberStart}${verse.verseNumberStart !== verse.verseNumberEnd ? `-${verse.verseNumberEnd}` : ""} ${verse.verseContent}`;
			});
		});
		let chapterCitation = `\n${chapter.bookName} ${chapter.chapterNumber} - ${this.props.preferences.bibleTranslationId}`;
		let versesNoHtml = verses.replace(/<(?:.|\n)*?>/gm, "");
		return versesNoHtml + chapterCitation;
	};

	onClose = () => {
		this.setState({ open: false }, () => {
			setTimeout(this.props.onDrawerClose, 500);
		});
	};

	renderSermon() {
		const classes = this.props.classes;

		return this.state.sermon ? (
			<div className={classNames("customScrollbar", classes.content)}>
				<Typography variant="h6">{this.state.sermon.title}</Typography>
				<Typography variant="caption">
					By: {this.state.sermon.authorFirstName} {this.state.sermon.authorLastName}
				</Typography>
				<Divider />
				<Typography className={classes.contentBody} dangerouslySetInnerHTML={{ __html: this.state.sermon.body }} />
			</div>
		) : (
			<Typography variant="h5" align="center">
				<CircularProgress className={classes.loading} size={35} />
				<br />
				Loading Sermon
			</Typography>
		);
	}

	renderIllustration() {
		const classes = this.props.classes;

		return this.state.illustration ? (
			<div className={classNames("customScrollbar", classes.content)}>
				<Typography variant="h6">{this.state.illustration.title}</Typography>
				<Typography variant="caption">
					From: {this.state.illustration.authorFirstName} {this.state.illustration.authorLastName}
				</Typography>
				<Divider />
				<Typography className={classes.contentBody} dangerouslySetInnerHTML={{ __html: this.state.illustration.body }} />
			</div>
		) : (
			<Typography variant="h5" align="center">
				<CircularProgress className={classes.loading} size={35} />
				<br />
				Loading Illustration
			</Typography>
		);
	}

	renderBibleChapter() {
		const { classes } = this.props;
		const { bibleChapter } = this.state;

		if (!bibleChapter) {
			return (
				<Typography variant="h5" align="center">
					<CircularProgress className={classes.loading} size={35} />
					<br />
					Loading Bible Chapter
				</Typography>
			);
		}

		const versesGroupedByParagraph = this.groupChapterVerses(bibleChapter);
		const copyright = SupportedTranslations.find((t) => t.id === bibleChapter.translationId)!.copyrightHtml;
		const { bibleSearchViewSetting } = this.props.preferences;

		return (
			<div className={classNames("customScrollbar", classes.content)}>
				<Typography variant="h6">
					{bibleChapter.bookName} {bibleChapter.chapterNumber}
				</Typography>
				<Typography variant="caption">Translation: {bibleChapter.translationName}</Typography>
				<Divider />
				{versesGroupedByParagraph.map((group, index) => (
					<Typography key={index} className={classes.chapterParagraph}>
						{group.map((verse) => {
							let { verseNumberStart, verseNumberEnd, verseContent } = verse;
							let showVerseNumbers = bibleSearchViewSetting === "all" || bibleSearchViewSetting === "numbers";
							let showTranslationNotes = bibleSearchViewSetting === "all";

							if (!showTranslationNotes) {
								verseContent = SearchServiceHelper.removeTranslationNotes(verseContent);
							}
							return (
								<React.Fragment key={verseNumberStart}>
									{showVerseNumbers && (
										<span>
											{" "}
											<b className={classes.verseNumber}>
												{verseNumberStart}
												{verseNumberStart !== verseNumberEnd ? `-${verseNumberEnd}` : null}
											</b>{" "}
										</span>
									)}
									<span dangerouslySetInnerHTML={{ __html: verseContent }} />
								</React.Fragment>
							);
						})}
					</Typography>
				))}
				<div className={classes.bibleCopyright}>
					<Divider />
					<Typography variant="caption" dangerouslySetInnerHTML={{ __html: copyright }} />
				</div>
			</div>
		);
	}

	render() {
		const { classes, sermonId, illustrationId, scriptureReference } = this.props;
		const title =
			this.state.contentType === "Bible" && this.state.bibleChapter
				? `${this.state.bibleChapter.bookName} ${this.state.bibleChapter.chapterNumber}`
				: this.state.contentType;

		return (
			<Drawer
				open={this.state.open}
				variant="persistent"
				classes={{
					paper: classes.drawer,
				}}
				anchor="right"
			>
				<ListSubheader className={classes.title}>
					<IconButton onClick={this.onCopy} color="inherit" className={classes.copyButton} id="content-copy">
						<FileCopyIcon />
						<Typography variant="h6" color="inherit" style={{ marginLeft: 4 }}>
							Copy {title}
						</Typography>
					</IconButton>
					<Tooltip title={`Close ${this.state.contentType}`}>
						<IconButton onClick={this.onClose} className={classes.closeIcon} color="inherit">
							<ArrowForwardIos />
						</IconButton>
					</Tooltip>
				</ListSubheader>
				<Divider />
				{sermonId && this.renderSermon()}
				{illustrationId && this.renderIllustration()}
				{scriptureReference && this.renderBibleChapter()}
			</Drawer>
		);
	}
}

export function ContentDrawer(props:Props){
	const classes = useStyles();
	return <ContentDrawerComponent {...props} classes={classes} />;
}